import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  getSpanStatistics,
  type GetSpanStatisticsParams,
  type SpanStatistics,
  type SpanStatisticsBaselineWindow,
} from "../api/client";
import type { ServiceSlug } from "../api/client";
import type { TimeRange } from "../utils/time";

export interface UseSpanStatisticsOptions {
  serviceSlug: ServiceSlug | null;
  timeRange: TimeRange;
  serviceName?: string;
  serviceInstance?: string;
  spanName?: string;
  traceIds?: string[];
  spanIds?: string[];
  baseline?: SpanStatisticsBaselineWindow;
  enabled?: boolean;
  limit?: number;
  refreshToken?: number;
}

export interface UseSpanStatisticsResult {
  stats: SpanStatistics[];
  loading: boolean;
  isFetching: boolean;
  error: string | null;
}

export function useSpanStatistics({
  serviceSlug,
  timeRange,
  serviceName,
  serviceInstance,
  spanName,
  traceIds = [],
  spanIds = [],
  baseline,
  enabled = true,
  limit,
  refreshToken = 0,
}: UseSpanStatisticsOptions): UseSpanStatisticsResult {
  const traceIdsKey = traceIds.join(",");
  const spanIdsKey = spanIds.join(",");

  const query = useQuery({
    queryKey: [
      "spanStatistics",
      serviceSlug,
      timeRange.from.toISOString(),
      timeRange.to.toISOString(),
      serviceName ?? null,
      serviceInstance ?? null,
      spanName ?? null,
      traceIdsKey,
      spanIdsKey,
      baseline ?? null,
      limit ?? null,
      refreshToken,
    ],
    enabled: !!serviceSlug && enabled,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => {
      const params: GetSpanStatisticsParams = {
        serviceSlug: serviceSlug!,
        startTime: timeRange.from,
        endTime: timeRange.to,
        serviceName,
        serviceInstance,
        spanName,
        traceIds: traceIds.length > 0 ? traceIds : undefined,
        spanIds: spanIds.length > 0 ? spanIds : undefined,
        baseline,
        groupBy: "service_name,span_name",
        limit,
        signal,
      };
      return getSpanStatistics(params);
    },
  });

  if (!serviceSlug || !enabled) {
    return { stats: [], loading: false, isFetching: false, error: null };
  }

  const error =
    query.error instanceof Error
      ? query.error.message
      : query.error
        ? "Failed to load span statistics"
        : null;
  return {
    stats: query.data ?? [],
    loading: query.isLoading,
    isFetching: query.isFetching,
    error,
  };
}
